type AccountLifecycle = {
  status?: string | null;
  state?: string | null;
  active_restrictions?: string[] | null;
};

const CLOSURE_RESTRICTIONS = new Set([
  'ACCT_MAINT_CLOSURE',
  'ACCT_MAINT_CLOSURE_PREP_BY_CORRESPONDENT',
  'ACCT_MAINT_CLOSURE_PREP_BY_CUSTODIAN',
]);

export const isClosedOrClosingAccount = (account?: AccountLifecycle | null): boolean =>
  [account?.status, account?.state].some((status) => status?.trim().toLowerCase() === 'closed') ||
  (account?.active_restrictions ?? []).some((restriction) => CLOSURE_RESTRICTIONS.has(restriction));

type LinkedAccount = AccountLifecycle & {
  account_id: string;
  custodian_code?: string;
  custodian_account_id?: string;
};

type WealthSummary = { total_assets: number; total_liabilities: number; net_worth: number };

/** Resolve current visibility without changing historical chart points or source records. */
export async function resolveCurrentWealthAccounts<
  Account extends LinkedAccount,
  Snapshot extends { accounts: Account[]; summary: WealthSummary },
>(
  snapshot: Snapshot,
  loadApexLifecycle: (accountId: string) => Promise<AccountLifecycle>,
  loadScopedSnapshot: (accountIds: string[]) => Promise<Snapshot | null>,
): Promise<Snapshot> {
  const visibility = await Promise.all(
    snapshot.accounts.map(async (account) => {
      if (isClosedOrClosingAccount(account)) return false;
      if (account.custodian_code !== 'APEX') return true;
      // Live closure projections historically used "inactive" in firm-data.
      if (account.status?.trim().toLowerCase() === 'inactive') return false;
      if (!account.custodian_account_id) return true;
      // Imported accounts can still say open after Apex has closed them.
      const lifecycle = await loadApexLifecycle(account.custodian_account_id);
      if (!lifecycle?.state) throw new Error('Apex account lifecycle is unavailable');
      return !isClosedOrClosingAccount(lifecycle);
    }),
  );
  const accounts = snapshot.accounts.filter((_, index) => visibility[index]);
  if (accounts.length === snapshot.accounts.length) return snapshot;
  const summary = accounts.length
    ? (await loadScopedSnapshot(accounts.map((account) => account.account_id)))?.summary
    : { total_assets: 0, total_liabilities: 0, net_worth: 0 };
  if (!summary) throw new Error('Current account summary is unavailable');
  return { ...snapshot, accounts, summary };
}
